import React, { useState, useEffect } from "react";
import classes from './SuppliersPersonalLedger.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';
import Footer from "../../Components/Footer/Footer";

const SupplierPersonalLedger = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Get customer data from navigation state
    const supplierName = location.state?.customerName || "Supplier Name"; // Keep as customerName for now since that's what you're passing
    const supplierId = location.state?.customerId || null;
    const supplierDetails = location.state?.customerDetails || null;
    const filteredTransactions = location.state?.filteredInvoices || []; // These are actually supplier ledger entries
    const totalPurchases = location.state?.totalSales || 0; // Total purchases from supplier
    const totalPaid = location.state?.totalPaid || 0;
    const totalOutstanding = location.state?.totalOutstanding || 0;
    const totalInvoices = location.state?.totalInvoices || 0;

    // Convert invoice data to ledger entries
    const [ledgerData, setLedgerData] = useState([]);

    // Helper functions for currency formatting
    const formatMoney = (value) => {
        const num = Number(value || 0);
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
    };

    const getPaymentStatus = (debtAmount, totalAmount) => {
        const debtNum = parseFloat(debtAmount || 0);
        const totalNum = parseFloat(totalAmount || 0);

        if (debtNum === 0) {
            return "Paid";
        } else if (debtNum === totalNum) {
            return "Owing";
        } else {
            return "Partial";
        }
    };

    useEffect(() => {
        // Transform supplier ledger data to personal ledger entries
        if (filteredTransactions && filteredTransactions.length > 0) {
            const transformedData = filteredTransactions.map((transaction, index) => {
                const totalAmount = parseFloat(transaction.balance || transaction.amount || 0);
                const debitAmount = parseFloat(transaction.debit || 0);
                const creditAmount = parseFloat(transaction.credit || 0);

                // For suppliers: debit = what we owe them, credit = what we paid them
                return {
                    id: transaction.id || `${index}`,
                    date: transaction.transaction_date || transaction.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
                    ref: transaction.invoice_number || transaction.uuid || `REF-${transaction.id || index}`,
                    description: transaction.description || 'Supplier Transaction',
                    account: '2100-Accounts Payable', // Changed from Receivable to Payable for suppliers
                    debit: debitAmount > 0 ? formatMoney(debitAmount) : '',
                    credit: creditAmount > 0 ? formatMoney(creditAmount) : '',
                    balance: formatMoney(totalAmount),
                    type: 'supplier_transaction',
                    rawDebit: debitAmount,
                    rawCredit: creditAmount,
                    rawBalance: totalAmount
                };
            });

            // Sort by date
            const sortedData = transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
            setLedgerData(sortedData);
        } else {
            // Fallback to supplier details ledgers if available
            if (supplierDetails && supplierDetails.ledgers) {
                const transformedLedgers = supplierDetails.ledgers.map((ledger, index) => ({
                    id: ledger.id || index,
                    date: ledger.transaction_date || ledger.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
                    ref: ledger.reference || ledger.invoice_number || ledger.uuid || `REF-${ledger.id || index}`,
                    description: ledger.description || 'Supplier Transaction',
                    account: '2100-Accounts Payable',
                    debit: ledger.debit ? formatMoney(ledger.debit) : '',
                    credit: ledger.credit ? formatMoney(ledger.credit) : '',
                    balance: formatMoney(ledger.balance || 0),
                    type: 'ledger',
                    rawDebit: parseFloat(ledger.debit || 0),
                    rawCredit: parseFloat(ledger.credit || 0),
                    rawBalance: parseFloat(ledger.balance || 0)
                }));
                setLedgerData(transformedLedgers);
            }
        }
    }, [filteredTransactions, supplierDetails]);


    const [selectedRows, setSelectedRows] = useState([]);
    const allSelected = selectedRows.length === ledgerData.length;

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedRows([]);
        } else {
            setSelectedRows(ledgerData.map((_, i) => i));
        }
    };

    const handleSelectRow = (index) => {
        if (selectedRows.includes(index)) {
            setSelectedRows(selectedRows.filter(i => i !== index));
        } else {
            setSelectedRows([...selectedRows, index]);
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    // Calculate running balance
    // (For suppliers: debit increases what we owe, credit decreases what we owe)
    let runningBalance = 0;
    const dataWithRunningBalance = ledgerData.map(row => {
        const debitAmount = row.rawDebit || parseFloat(row.debit.replace(/,/g, '') || 0);
        const creditAmount = row.rawCredit || parseFloat(row.credit.replace(/,/g, '') || 0);

        // For suppliers: debit increases liability, credit decreases liability
        runningBalance += debitAmount - creditAmount;

        return {
            ...row,
            runningBalance: formatMoney(Math.abs(runningBalance))
        };
    });

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns} onClick={() => navigate("/transactions/receivables/sales")}>
                        New Sales Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/inventory/purchaseOrder")}>
                        New Purchase Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/customer-sales")}>
                        All Invoices
                    </button>
                    <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>
                        Product & Services
                    </button>
                </div>

                {/* Form and Icons */}
                <div className={classes.act}>
                    <form className={classes.form}>
                        <div className={classes.group}>
                            <label>Period:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>Current month</option>
                                    <option>Last month</option>
                                    <option>This year</option>
                                    <option>Custom range</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>

                        <div className={classes.group1}>
                            <label>Group by:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>None</option>
                                    <option>Date</option>
                                    <option>Transaction Type</option>
                                    <option>Amount</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>Start Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>01/01/25</option>
                                    <option>01/12/24</option>
                                    <option>01/11/24</option>
                                    <option>01/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>{new Date().toLocaleDateString('en-GB')}</option>
                                    <option>31/12/24</option>
                                    <option>30/11/24</option>
                                    <option>31/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>

                        <div className={classes.group1}>
                            <button className={classes.greenBtn} type="button">
                                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                                Run Report
                            </button>
                        </div>
                    </form>

                    <div className={classes.iconbars}>
                        <div className={classes.iconbarr}>
                            <div className={classes.iconbar}>
                                <Filter size={16} style={{ marginRight: "6px" }} />
                                Filter
                            </div>
                            <div className={classes.iconbar}>
                                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                                Schedule
                            </div>
                        </div>

                        <div className={classes.iconbarss}>
                            <div className={classes.iconbar}>
                                <Mail size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Printer size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Settings size={16} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back Button */}
                <div className={classes.matron}>
                    <div>
                        <button
                            className={classes.backButton}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                        </button>
                    </div>

                    <div>
                        <p className={classes.toplea}>Supplier's Personal Ledger</p>
                        <p className={classes.topl}>Financial position as of {new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                {/* Customer Summary Cards */}

                {/* Report Content */}
                <div className={classes.containere}>
                    <header className={classes.headers}>
                        <h1 className={classes.companyName}>Promix Accounting Ltd</h1>
                        <h2 className={classes.title}>SUPPLIER'S PERSONAL LEDGER</h2>
                        <p className={classes.period}>For the period ended {new Date().toLocaleDateString()}</p>
                        <p className={classes.toplea}>
                            {supplierName}'s Personal Ledger
                        </p>
                        <button className={classes.unaudited}>unaudited</button>
                    </header>


                    <div className={classes.divider}></div>

                    {/* Table Container */}
                    <div className={classes.tableContainer}>
                        <table className={classes.table}>
                            <thead>
                                <tr className={classes.tableHeader}>
                                    <th>Transaction Date</th>
                                    <th>Reference</th>
                                    <th>Description</th>
                                    <th>Debit</th>
                                    <th>Credit</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataWithRunningBalance.length > 0 ? (
                                    dataWithRunningBalance.map((row, index) => (
                                        <tr key={row.id || index} className={classes.tableRow}>
                                            <td className={classes.tableCell}>{row.date}</td>
                                            <td className={classes.tableCell}>{row.ref}</td>
                                            <td className={classes.tableCell}>{row.description}</td>
                                            <td style={{ color: 'rgba(220, 38, 38, 1)', textAlign: 'right' }}>
                                                {row.debit ? `₦${row.debit}` : '—'}
                                            </td>
                                            <td style={{ color: 'rgba(22, 163, 74, 1)', textAlign: 'right' }}>
                                                {row.credit ? `₦${row.credit}` : '—'}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>₦{row.runningBalance}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                            No transactions found for {supplierName}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary Footer */}

                    {/* Footer Table */}
                    <table className={classes.footerTable}>
                        <tfoot>
                            <tr>
                                <td className={classes.frston}>First</td>
                                <td className={`${classes.page} ${classes.activePage}`}>1</td>
                                <td className={classes.twono}>2</td>
                                <td className={classes.lastno}>Last</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className={classes.lasone}>
                        <div className={classes.paginationDisplay}>
                            Displaying 1 - {dataWithRunningBalance.length} of {dataWithRunningBalance.length}
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default SupplierPersonalLedger;